import { useMemo } from "react";

import { IconCoinPound, IconPencil, IconTrash } from "@tabler/icons";
import { useTheme } from "styled-components";

import { TableThWithSortIcon } from "@sso/components/TableThWithSortIcon";
import { Dialog } from "@sso/constant";
import { useAppDispatch } from "@sso/hooks/useRedux";
import { openDialog } from "@sso/store/slices/dialog";
import { setPricingSortColumn } from "@sso/store/slices/pricing";
import { PricingSummary, TableColumn, PricingSummaryKeys } from "@sso/types";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";
import { LayoutTable, TD, TH, TableWrapper } from "@sso/ui/Table";

interface PricingsTableProps {
  data: PricingSummary[];
  tableColumns: TableColumn<string>[];
  dataKeys?: any;
}

const PricingsTable = ({ data, tableColumns, dataKeys }: PricingsTableProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const handleSortAction = (column: string) => {
    dispatch(setPricingSortColumn(column));
  };

  const rows = useMemo(
    () =>
      data.map(pricing => ({
        ...pricing,
        [PricingSummaryKeys.DATE_OF_EFFECT]: new Date(pricing.dateOfEffect).toLocaleDateString(),
        [PricingSummaryKeys.UPDATE_DATE]: new Date(pricing.updateDate).toLocaleDateString(),
      })),
    [data],
  );

  const handleOnRemoveClick = (id: string) => {
    dispatch(openDialog({ type: Dialog.DELETE_PRICING, id }));
  };

  const handleEditPricing = (id: string, currencies: string) => {
    if (currencies === "GBP") {
      dispatch(openDialog({ type: Dialog.EDIT_PRICING, id }));
    } else {
      dispatch(openDialog({ type: Dialog.EDIT_MULTI_CURRENCY_PRICING, id }));
    }
  };

  const openAddCurrencyRules = (id: string) => {
    dispatch(openDialog({ type: Dialog.PRICING_ADD_CURRENCY_RULES, id }));
  };

  return (
    <TableWrapper>
      <LayoutTable>
        <thead>
          <tr>
            {tableColumns.map(col => (
              <TH key={col.field} sortable={col.sortable} onClick={() => col.sortable && handleSortAction(col.field)}>
                {col.sortable ? <TableThWithSortIcon column={col.field} title={col.name} /> : col.name}
              </TH>
            ))}
            <TH textAlign="center">Actions</TH>
          </tr>
        </thead>
        <tbody>
          {rows.map(pricing => (
            <tr key={pricing.pricingId}>
              {tableColumns
                .map(x => x.field as PricingSummaryKeys)
                .map(key => (
                  <TD key={key}>{pricing[key]}</TD>
                ))}
              <TD>
                <Flex>
                  <IconButton
                    color={theme.colors.grey[400]}
                    hoverColor={theme.colors.grey[700]}
                    onClick={() => openAddCurrencyRules(pricing.pricingId)}
                    title="Add currency rules"
                  >
                    <IconCoinPound />
                  </IconButton>
                  <IconButton
                    color={theme.colors.grey[400]}
                    hoverColor={theme.colors.grey[700]}
                    onClick={() => handleEditPricing(pricing.pricingId, pricing.currencies!)}
                    title="Edit pricing"
                  >
                    <IconPencil />
                  </IconButton>
                  <IconButton
                    color={theme.colors.grey[700]}
                    hoverColor={theme.colors.error.main}
                    onClick={() => handleOnRemoveClick(pricing.pricingId)}
                    title="Delete Pricing"
                  >
                    <IconTrash />
                  </IconButton>
                </Flex>
              </TD>
            </tr>
          ))}
        </tbody>
      </LayoutTable>
    </TableWrapper>
  );
};

export default PricingsTable;
